/**
 * Carrom Error Handler
 *
 * Wraps ErrorHandlingService for game-specific error classification.
 * All user-facing messages go through NotificationService — never raw alert().
 *
 * Follows LudoErrorHandler pattern.
 */

import {
  ErrorHandlingService,
  ErrorType,
  ErrorSeverity,
} from '../../services/ErrorHandlingService';
import {
  NotificationService,
  NotificationType,
} from '../../services/NotificationService';

export enum CarromErrorType {
  INVALID_SHOT = 'INVALID_SHOT',
  NOT_YOUR_TURN = 'NOT_YOUR_TURN',
  GAME_ENDED = 'GAME_ENDED',
  GAME_LOGIC = 'GAME_LOGIC',
  PHYSICS_ERROR = 'PHYSICS_ERROR',
  CONNECTION_ERROR = 'CONNECTION_ERROR',
  CORRUPTED_DATA = 'CORRUPTED_DATA',
  AI_TIMEOUT = 'AI_TIMEOUT',
  MULTIPLAYER_VALIDATION = 'MULTIPLAYER_VALIDATION',
  SAVE_ERROR = 'SAVE_ERROR',
  LOAD_ERROR = 'LOAD_ERROR',
  UNKNOWN = 'UNKNOWN',
}

export interface CarromError {
  type: CarromErrorType;
  message: string;
  details?: any;
  timestamp: number;
  recoverable: boolean;
}

export class CarromErrorHandler {
  private errorService: ErrorHandlingService;
  private notificationService: NotificationService;
  private errorLog: CarromError[] = [];
  private readonly MAX_ERROR_LOG = 50;

  constructor(
    errorService: ErrorHandlingService,
    notificationService: NotificationService
  ) {
    this.errorService = errorService;
    this.notificationService = notificationService;
  }

  handleError(
    type: CarromErrorType,
    message: string,
    details?: any,
    showNotification: boolean = true
  ): CarromError {
    const error: CarromError = {
      type,
      message,
      details,
      timestamp: Date.now(),
      recoverable: this.isRecoverable(type),
    };

    this.logError(error);

    const { errorType, severity } = this.classifyError(type);
    this.errorService.handleError(
      new Error(message),
      errorType,
      severity,
      { component: 'Carrom', additionalData: details },
      { showNotification: false }
    );

    if (showNotification) {
      this.showErrorNotification(error);
    }

    return error;
  }

  private classifyError(
    type: CarromErrorType
  ): { errorType: ErrorType; severity: ErrorSeverity } {
    switch (type) {
      case CarromErrorType.INVALID_SHOT:
      case CarromErrorType.NOT_YOUR_TURN:
      case CarromErrorType.GAME_ENDED:
      case CarromErrorType.GAME_LOGIC:
      case CarromErrorType.PHYSICS_ERROR:
      case CarromErrorType.AI_TIMEOUT:
        return { errorType: ErrorType.GAME_LOGIC, severity: ErrorSeverity.LOW };

      case CarromErrorType.CONNECTION_ERROR:
      case CarromErrorType.MULTIPLAYER_VALIDATION:
        return { errorType: ErrorType.MULTIPLAYER, severity: ErrorSeverity.MEDIUM };

      case CarromErrorType.CORRUPTED_DATA:
      case CarromErrorType.SAVE_ERROR:
      case CarromErrorType.LOAD_ERROR:
        return { errorType: ErrorType.STORAGE, severity: ErrorSeverity.MEDIUM };

      default:
        return { errorType: ErrorType.SYSTEM, severity: ErrorSeverity.MEDIUM };
    }
  }

  // Convenience methods
  handleGameLogicError(reason: string): CarromError {
    return this.handleError(
      CarromErrorType.GAME_LOGIC,
      `Game error: ${reason}`,
      { reason }
    );
  }

  handleNotYourTurn(): CarromError {
    return this.handleError(
      CarromErrorType.NOT_YOUR_TURN,
      "It's not your turn. Please wait.",
      undefined
    );
  }

  handlePhysicsError(error: any): CarromError {
    return this.handleError(
      CarromErrorType.PHYSICS_ERROR,
      'Physics simulation error. Resetting board.',
      error,
      true
    );
  }

  handleConnectionError(error: any): CarromError {
    return this.handleError(
      CarromErrorType.CONNECTION_ERROR,
      'Connection lost. Attempting to reconnect...',
      error,
      true
    );
  }

  handleCorruptedData(dataType: string, error: any): CarromError {
    return this.handleError(
      CarromErrorType.CORRUPTED_DATA,
      'Unable to load saved game. Starting new game.',
      { dataType, error },
      true
    );
  }

  handleSaveError(gameId: string, error: any): CarromError {
    return this.handleError(
      CarromErrorType.SAVE_ERROR,
      'Failed to save game. Your progress may be lost.',
      { gameId, error },
      true
    );
  }

  handleLoadError(gameId: string, error: any): CarromError {
    return this.handleError(
      CarromErrorType.LOAD_ERROR,
      'Failed to load game. Starting new game.',
      { gameId, error },
      true
    );
  }

  // Internal helpers
  private isRecoverable(type: CarromErrorType): boolean {
    switch (type) {
      case CarromErrorType.INVALID_SHOT:
      case CarromErrorType.NOT_YOUR_TURN:
      case CarromErrorType.GAME_ENDED:
      case CarromErrorType.GAME_LOGIC:
      case CarromErrorType.PHYSICS_ERROR:
      case CarromErrorType.CONNECTION_ERROR:
      case CarromErrorType.CORRUPTED_DATA:
      case CarromErrorType.AI_TIMEOUT:
      case CarromErrorType.SAVE_ERROR:
      case CarromErrorType.LOAD_ERROR:
        return true;
      default:
        return false;
    }
  }

  private logError(error: CarromError): void {
    this.errorLog.push(error);
    if (this.errorLog.length > this.MAX_ERROR_LOG) {
      this.errorLog.shift();
    }
  }

  private showErrorNotification(error: CarromError): void {
    const notificationType = error.recoverable
      ? NotificationType.WARNING
      : NotificationType.ERROR;
    this.notificationService.addNotification({
      type: notificationType,
      title: 'Carrom Error',
      message: error.message,
      duration: 5000,
    });
  }

  getErrorLog(): CarromError[] {
    return [...this.errorLog];
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }
}
